import React, { useState, useEffect } from 'react';
import { getTags } from '../../services/campaignTags';
import type { CampaignTag } from '../../types/campaign';
import { TagBadge } from './TagBadge';

interface FilterPanelProps {
  status: string[];
  onStatusChange: (status: string[]) => void;
  managerId?: string;
  onManagerChange: (managerId: string | undefined) => void;
  tagIds?: number[];
  onTagIdsChange: (tagIds: number[] | undefined) => void;
  createdFrom?: string;
  createdTo?: string;
  onDateRangeChange: (from?: string, to?: string) => void;
  onReset: () => void;
  hasActiveFilters: boolean;
}

const STATUS_OPTIONS = [
  { value: 'active', label: 'Active' },
  { value: 'paused', label: 'Paused' },
  { value: 'archived', label: 'Archived' },
];

const MANAGER_OPTIONS = [
  { value: 'me', label: 'My Campaigns' },
  { value: 'unassigned', label: 'Unassigned' },
];

export const FilterPanel: React.FC<FilterPanelProps> = ({
  status,
  onStatusChange,
  managerId,
  onManagerChange,
  tagIds = [],
  onTagIdsChange,
  createdFrom,
  createdTo,
  onDateRangeChange,
  onReset,
  hasActiveFilters,
}) => {
  const [availableTags, setAvailableTags] = useState<CampaignTag[]>([]);
  const [loadingTags, setLoadingTags] = useState(false);

  useEffect(() => {
    const fetchTags = async () => {
      try {
        setLoadingTags(true);
        const tags = await getTags();
        setAvailableTags(tags);
      } catch (error) {
        console.error('Failed to fetch tags:', error);
      } finally {
        setLoadingTags(false);
      }
    };
    fetchTags();
  }, []);

  const handleStatusToggle = (value: string) => {
    if (status.includes(value)) {
      onStatusChange(status.filter(s => s !== value));
    } else {
      onStatusChange([...status, value]);
    }
  };

  const handleTagToggle = (tagId: number) => {
    const currentTags = tagIds || [];
    if (currentTags.includes(tagId)) {
      const newTags = currentTags.filter(id => id !== tagId);
      onTagIdsChange(newTags.length > 0 ? newTags : undefined);
    } else {
      onTagIdsChange([...currentTags, tagId]);
    }
  };

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-4 space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-medium text-gray-900">Filters</h3>
        {hasActiveFilters && (
          <button
            onClick={onReset}
            className="text-sm text-indigo-600 hover:text-indigo-700 font-medium"
          >
            Clear all
          </button>
        )}
      </div>

      {/* Status Filter */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Status
        </label>
        <div className="space-y-2">
          {STATUS_OPTIONS.map(option => (
            <label key={option.value} className="flex items-center">
              <input
                type="checkbox"
                checked={status.includes(option.value)}
                onChange={() => handleStatusToggle(option.value)}
                className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
              />
              <span className="ml-2 text-sm text-gray-700">{option.label}</span>
            </label>
          ))}
        </div>
      </div>

      {/* Manager Filter */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Assigned To
        </label>
        <select
          value={managerId || ''}
          onChange={(e) => onManagerChange(e.target.value || undefined)}
          className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
        >
          <option value="">All Campaigns</option>
          {MANAGER_OPTIONS.map(option => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </div>

      {/* Tags Filter */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Tags
        </label>
        {loadingTags ? (
          <p className="text-sm text-gray-500">Loading tags...</p>
        ) : availableTags.length > 0 ? (
          <div className="space-y-2 max-h-48 overflow-y-auto">
            {availableTags.map(tag => (
              <label key={tag.id} className="flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={tagIds?.includes(tag.id) || false}
                  onChange={() => handleTagToggle(tag.id)}
                  className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                />
                <span className="ml-2">
                  <TagBadge tag={tag} />
                </span>
              </label>
            ))}
          </div>
        ) : (
          <p className="text-sm text-gray-500">No tags available</p>
        )}
      </div>

      {/* Date Range Filter */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Created Date
        </label>
        <div className="space-y-2">
          <div>
            <label className="block text-xs text-gray-500 mb-1">From</label>
            <input
              type="date"
              value={createdFrom || ''}
              onChange={(e) => onDateRangeChange(e.target.value || undefined, createdTo)}
              className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
            />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">To</label>
            <input
              type="date"
              value={createdTo || ''}
              onChange={(e) => onDateRangeChange(createdFrom, e.target.value || undefined)}
              className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

FilterPanel.displayName = 'FilterPanel';
